export function parseFormDate(value: unknown): Date | null {
  if (value == null || value === '') {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Compare calendar dates (local midnight) like GlobalTranz create-load validation.
 * If either value is missing or unparseable, validation is skipped (ok).
 */
export function validateDeliveryOnOrAfterPickupDate(
  pickupValue: unknown,
  deliveryValue: unknown,
  options?: { compareTime?: boolean }
): { ok: true } | { ok: false; message: string } {
  const pickup = parseFormDate(pickupValue);
  const delivery = parseFormDate(deliveryValue);
  if (!pickup || !delivery) {
    return { ok: true };
  }

  const p = new Date(pickup);
  const d = new Date(delivery);

  if (!options?.compareTime) {
    p.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
  }

  if (d < p) {
    return {
      ok: false,
      message: options?.compareTime
        ? 'Delivery time must be on or after pickup time.'
        : 'Delivery date must be on or after pickup date.',
    };
  }
  return { ok: true };
}

export function validateDuplicateStopLocations(
  pickupExternalTMSID: unknown,
  consigneeExternalTMSID: unknown
): { ok: true } | { ok: false; message: string } {
  const pickupId =
    typeof pickupExternalTMSID === 'string' ? pickupExternalTMSID.trim() : '';
  const consigneeId =
    typeof consigneeExternalTMSID === 'string'
      ? consigneeExternalTMSID.trim()
      : '';

  if (!pickupId || !consigneeId || pickupId !== consigneeId) {
    return { ok: true };
  }

  return {
    ok: false,
    message: 'Pickup/Consignee locations cannot be the same.',
  };
}

export function validateMonotonicRouteStopTimes(
  stops: Array<{ apptStartTime?: unknown; apptEndTime?: unknown }>
): { ok: true } | { ok: false; message: string; pathIndex: number } {
  for (let i = 0; i < stops.length - 1; i++) {
    const currentStop = stops[i];
    const nextStop = stops[i + 1];
    const currentEnd = parseFormDate(currentStop.apptEndTime);
    const currentTime = currentEnd
      ? currentStop.apptEndTime
      : currentStop.apptStartTime;
    const nextTime = nextStop.apptStartTime;
    const result = validateDeliveryOnOrAfterPickupDate(currentTime, nextTime, {
      compareTime: true,
    });

    if (!result.ok) {
      return {
        ok: false,
        message: `Stop ${i + 2} must be scheduled on or after stop ${i + 1}.`,
        pathIndex: i + 1,
      };
    }
  }

  return { ok: true };
}

export function validateDuplicateRouteStopLocations(
  stops: Array<{ externalTMSID?: string }>
): { ok: true } | { ok: false; message: string; pathIndex: number } {
  const seen = new Map<string, number>();

  for (let i = 0; i < stops.length; i++) {
    const stop = stops[i];
    const externalTMSID =
      typeof stop?.externalTMSID === 'string' ? stop.externalTMSID.trim() : '';

    if (!externalTMSID) {
      continue;
    }

    if (seen.has(externalTMSID)) {
      return {
        ok: false,
        message: 'Stop locations cannot repeat.',
        pathIndex: i,
      };
    }

    seen.set(externalTMSID, i);
  }

  return { ok: true };
}
